import { createApi } from "@reduxjs/toolkit/query/react";

import { fromBackendDate } from "@tsp-core/datetime/utils";
import { baseQuery } from "@tsp-core/store/utils";

import { CurrencyId } from "@tsp-domains/money/enums";
import { Money } from "@tsp-domains/money/types";
import { parseCurrencyId, transformFromStorableMoney } from "@tsp-domains/money/utils";

import {
  SupplierActionHistoryItemMetadata,
  SupplierActionHistoryList,
  SupplierCurrencyEarnings,
  SupplierEarningsOverview,
  SupplierFundsOverview,
} from "../types";

type GetSupplierFundsOverviewResponseData = {
  currencyFunds: Array<{
    currency: {
      id: number;
      name: string;
    };
    internalFundsAmountStorable: number;
    generatedAmountStorable: number;
    inProgressAmountStorable: number;
    paidAmountStorable: {
      currentMonth: { month: number; amountStorable: number };
      previousMonth: { month: number; amountStorable: number };
    };
    delayedAmountStorable: number;
  }>;
};

type GetSupplierCurrencyEarningsResponseData = {
  currency: {
    id: number;
    name: string;
  };
  pendingPaymentsTotalAmountStorable: number;
  yearlyEarningsAmountStorable: number;
};

type GetSupplierEarningsOverviewResponseData = {
  currencyEarnings: Array<GetSupplierCurrencyEarningsResponseData>;
  yearlyRegisteredWorkCount: number;
  pendingApprovalWorkCount: number;
};

function toSupplierCurrencyEarnings(item?: GetSupplierCurrencyEarningsResponseData): SupplierCurrencyEarnings {
  return {
    pendingPaymentsTotalAmount:
      transformFromStorableMoney(item?.pendingPaymentsTotalAmountStorable ?? 0) ?? (0 as Money),
    yearlyEarningsAmount: transformFromStorableMoney(item?.yearlyEarningsAmountStorable ?? 0) ?? (0 as Money),
  };
}

function getDashboardCurrencyEarnings(
  items: Array<GetSupplierCurrencyEarningsResponseData>,
): SupplierEarningsOverview["currencyEarnings"] {
  const byCurrencyId = new Map(items.map((item) => [item.currency.id, item]));

  return {
    [CurrencyId.EUR]: toSupplierCurrencyEarnings(byCurrencyId.get(CurrencyId.EUR)),
    [CurrencyId.USD]: toSupplierCurrencyEarnings(byCurrencyId.get(CurrencyId.USD)),
  };
}

type GetSupplierActionHistoryListResponseData = {
  items: Array<{
    uuid: string;
    actionType: string;
    createdAt: number;
    metadata: {
      workItemUuid: string;
      workTypeId: number;
      workSubTypeId: string;
      isScheduled?: boolean;
      totalAmountStorable?: number;
      entityType?: string;
      currencyId?: number;
    } | null;
  }>;
  pagination: {
    offset: number;
    limit: number;
    count: number;
  };
};

function toSupplierActionHistoryMetadata(
  metadata: GetSupplierActionHistoryListResponseData["items"][number]["metadata"],
): SupplierActionHistoryItemMetadata | null {
  if (!metadata) {
    return null;
  }

  const currencyId = metadata.currencyId != null ? parseCurrencyId(metadata.currencyId) : null;

  return {
    ...metadata,
    currencyId: currencyId ?? undefined,
  };
}

type GetSupplierActionHistoryListParams = {
  page?: number;
  limit?: number;
};

export const dashboardApi = createApi({
  reducerPath: "@dashboard/api",
  baseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false,
  keepUnusedDataFor: 0,
  tagTypes: ["SupplierEarningsOverview", "SupplierFundsOverview", "SupplierActionHistoryList"],
  endpoints: (builder) => ({
    getSupplierFundsOverview: builder.query<SupplierFundsOverview, void, GetSupplierFundsOverviewResponseData>({
      query: () => ({
        url: "/dashboard/getSupplierFundsOverview",
        method: "GET",
      }),
      transformResponse: (response: GetSupplierFundsOverviewResponseData): SupplierFundsOverview => ({
        currencyFunds: (response.currencyFunds ?? []).map((item) => ({
          currency: {
            ...item.currency,
            id: parseCurrencyId(item.currency.id),
          },
          internalFundsAmount: transformFromStorableMoney(item.internalFundsAmountStorable),
          generatedAmount: transformFromStorableMoney(item.generatedAmountStorable),
          inProgressAmount: transformFromStorableMoney(item.inProgressAmountStorable),
          paidAmount: {
            currentMonth: {
              month: item.paidAmountStorable.currentMonth.month,
              amount: transformFromStorableMoney(item.paidAmountStorable.currentMonth.amountStorable),
            },
            previousMonth: {
              month: item.paidAmountStorable.previousMonth.month,
              amount: transformFromStorableMoney(item.paidAmountStorable.previousMonth.amountStorable),
            },
          },
          delayedAmount: transformFromStorableMoney(item.delayedAmountStorable),
        })),
      }),
      providesTags: ["SupplierFundsOverview"],
    }),
    getSupplierEarningsOverview: builder.query<SupplierEarningsOverview, void, GetSupplierEarningsOverviewResponseData>(
      {
        query: () => {
          return {
            url: "/dashboard/getSupplierEarningsOverview",
            method: "GET",
          };
        },
        transformResponse: (response: GetSupplierEarningsOverviewResponseData): SupplierEarningsOverview => {
          return {
            currencyEarnings: getDashboardCurrencyEarnings(response.currencyEarnings ?? []),
            yearlyRegisteredWorkCount: response.yearlyRegisteredWorkCount,
            pendingApprovalWorkCount: response.pendingApprovalWorkCount,
          };
        },
        providesTags: ["SupplierEarningsOverview"],
      },
    ),
    getSupplierActionHistoryList: builder.query<
      SupplierActionHistoryList,
      GetSupplierActionHistoryListParams | void,
      GetSupplierActionHistoryListResponseData
    >({
      query: (params) => {
        return {
          url: "/dashboard/getSupplierActionHistoryList",
          method: "GET",
          params,
        };
      },
      transformResponse: (response: GetSupplierActionHistoryListResponseData): SupplierActionHistoryList => {
        return {
          items: response.items.map((item) => ({
            uuid: item.uuid,
            actionType: item.actionType,
            createdAt: fromBackendDate(item.createdAt),
            metadata: toSupplierActionHistoryMetadata(item.metadata),
          })),
          pagination: response.pagination,
        };
      },
      providesTags: ["SupplierActionHistoryList"],
    }),
  }),
});
